import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { ApiRoutes, AppRoutes, getDashboardRoute } from '../constants/routes';
import { fetcher } from './api';
import { claimPendingReferral } from './referrals';

export const AuthContext = createContext();

// Listening mode survives a page refresh - per tab, like a session
const LISTENING_MODE_KEY = 'tuneswell:listening-mode';
const readSavedListeningMode = () => {
  try {
    return JSON.parse(sessionStorage.getItem(LISTENING_MODE_KEY)) || {};
  } catch {
    return {};
  }
};

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  
  const [isAuthed, setIsAuthed] = useState(false);
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [savedMode] = useState(readSavedListeningMode);
  const [isCreatorListeningMode, setIsCreatorListeningMode] = useState(!!savedMode.creator);
  const [isActListeningMode, setIsActListeningMode] = useState(!!savedMode.act);
  const [isLabelListeningMode, setIsLabelListeningMode] = useState(!!savedMode.label);
  const [isPublisherListeningMode, setIsPublisherListeningMode] = useState(!!savedMode.publisher);
  const [isPodcasterListeningMode, setIsPodcasterListeningMode] = useState(!!savedMode.podcaster);
  // In label listening mode: the label being curated for ({ id, name }), or
  // null to curate as yourself. Label accounts don't need it - the server
  // files their playlists under their label automatically.
  const [curatingLabel, setCuratingLabel] = useState(savedMode.curatingLabel || null);
  const [currentActId, setCurrentActId] = useState(savedMode.actId || null);
  // The label or publisher this person is working as ({ kind, id, name }),
  // chosen from the header's "acting as" switcher. Their own account type
  // doesn't matter: a listener who runs a label works as it the same way.
  const [workingAs, setWorkingAs] = useState(savedMode.workingAs || null);

  useEffect(() => {
    try {
      sessionStorage.setItem(LISTENING_MODE_KEY, JSON.stringify({
        creator: isCreatorListeningMode,
        act: isActListeningMode,
        actId: currentActId,
        label: isLabelListeningMode,
        curatingLabel,
        publisher: isPublisherListeningMode,
        podcaster: isPodcasterListeningMode,
        workingAs,
      }));
    } catch {
      // Storage unavailable - the mode just won't survive a refresh
    }
  }, [isCreatorListeningMode, isActListeningMode, currentActId, isLabelListeningMode, curatingLabel, isPublisherListeningMode, isPodcasterListeningMode, workingAs]);

  // Check for existing token and restore session on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Make a request to verify the token and get user data
          const userData = await fetcher(ApiRoutes.User);
          setUser(userData);
          setUserType(userData.user_type || 'listener');
          setIsAuthed(true);
        } catch (error) {
          console.error('Error checking auth status:', error);
          // Clear invalid token silently - don't trigger login attempts
          localStorage.removeItem('token');
          setIsAuthed(false);
          setUser(null);
          setUserType(null);
        }
      } else {
        setIsAuthed(false);
        setUser(null);
        setUserType(null);
      }
      setIsLoading(false);
    };

    checkAuthStatus();
  }, []);

  // Reload the signed-in account, e.g. after starting a label or publisher.
  // The login response doesn't list the person's organizations; this does.
  const refreshUser = async () => {
    try {
      const userData = await fetcher(ApiRoutes.User);
      setUser(userData);
      // e.g. a listener who started an artist profile is now a creator
      if (userData?.user_type) setUserType(userData.user_type);
      return userData;
    } catch (error) {
      return null;
    }
  };

  const authorizeUser = (user=null) => {
    console.log('AuthProvider - authorizeUser called with:', user);
    console.log('AuthProvider - user.user_type:', user?.user_type);
    return new Promise((res) => {
      setUser(user);
      const newUserType = user?.user_type || 'listener';
      console.log('AuthProvider - Setting userType to:', newUserType);
      setUserType(newUserType);
      setIsAuthed(true);
      
      // Invalidate queries to ensure fresh data on login
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['user-pending-invitations']);
      queryClient.invalidateQueries(['user-connections']);
      queryClient.invalidateQueries(['user-declined-connections']);
      queryClient.invalidateQueries(['creator-acts']);
      refreshUser();
      
      res();
    });
    }

  const deauthorizeUser  = () => {
    console.log('logging out')
    setUser(null)
    setUserType(null)
    setIsCreatorListeningMode(false)
    setIsActListeningMode(false)
    setIsLabelListeningMode(false)
    setIsPublisherListeningMode(false)
    setIsPodcasterListeningMode(false)
    setCuratingLabel(null)
    setCurrentActId(null)
    setWorkingAs(null)
    localStorage.removeItem('token'); // Clear the token from localStorage
    
    // Reset (not just clear) the query cache: wipes the previous user's data
    // and refetches whatever is on screen anonymously. clear() alone left
    // mounted public pages (e.g. an album) stuck on results that failed with
    // the expired token.
    queryClient.resetQueries();
    
    return new Promise((res) => {
      setIsAuthed(false);
      res();
    });
  }

  // Previously, an expired/invalid token detected mid-session (utils/api.js)
  // only cleared the stored token and threw an error back to whichever
  // query called it - isAuthed stayed true, cached data stayed visible, and
  // nothing redirected away from the current (now actually-unauthenticated)
  // page until the user happened to refresh and the mount-time check below
  // re-ran against the now-missing token. Listening for the event api.js
  // dispatches lets this react immediately, the same way a manual logout
  // does - clearing auth state and the query cache, and letting whatever
  // route guard is on the current page redirect once isAuthed flips.
  useEffect(() => {
    const handleSessionExpired = () => {
      deauthorizeUser();
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Someone who opened a shared link before signing in: credit whoever
  // shared it, now that there's an account to credit them against
  useEffect(() => {
    if (user?.uuid) claimPendingReferral();
  }, [user?.uuid]);

  // Listening, in any of its forms, ends working as an organization
  const enterCreatorListeningMode = () => {
    setWorkingAs(null);
    setIsCreatorListeningMode(true);
  }

  const exitCreatorListeningMode = () => {
    setIsCreatorListeningMode(false);
  }

  const enterActListeningMode = (actId) => {
    setWorkingAs(null);
    setIsActListeningMode(true);
    setCurrentActId(actId);
  }

  const exitActListeningMode = () => {
    setIsActListeningMode(false);
    setCurrentActId(null);
  }

  const enterLabelListeningMode = (label = null) => {
    setWorkingAs(null);
    setCuratingLabel(label);
    setIsLabelListeningMode(true);
  }

  const exitLabelListeningMode = () => {
    setIsLabelListeningMode(false);
    setCuratingLabel(null);
  }

  const enterPublisherListeningMode = () => {
    setWorkingAs(null);
    setIsPublisherListeningMode(true);
  }

  const enterPodcasterListeningMode = () => {
    setWorkingAs(null);
    setIsPodcasterListeningMode(true);
  }

  const leaveAllListeningModes = () => {
    setIsCreatorListeningMode(false);
    setIsActListeningMode(false);
    setCurrentActId(null);
    setIsLabelListeningMode(false);
    setCuratingLabel(null);
    setIsPublisherListeningMode(false);
    setIsPodcasterListeningMode(false);
  }

  // Change the organization being worked as. Saved now, not after the
  // next render, because the next page's requests name it (utils/api.js
  // actingAsHeaders); pages cached for another organization are dropped.
  const changeWorkingAs = (organization) => {
    const switching = organization?.kind !== workingAs?.kind || organization?.id !== workingAs?.id;
    setWorkingAs(organization);
    if (!switching) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(LISTENING_MODE_KEY)) || {};
      sessionStorage.setItem(LISTENING_MODE_KEY, JSON.stringify({ ...saved, workingAs: organization }));
    } catch {
      // Storage unavailable - requests act on the person's first organization
    }
    if (organization && workingAs) queryClient.resetQueries();
  }

  // Work as a label or publisher: its dashboard, not the listening pages
  const workAsOrganization = (organization) => {
    leaveAllListeningModes();
    changeWorkingAs(organization);
  }

  // Back to the account's own dashboard (creators, podcasters, and label or
  // publisher accounts), not listening
  const workAsAccount = () => {
    leaveAllListeningModes();
    changeWorkingAs(null);
  }

  // "Me": listening, for any account type
  const listenAsMe = () => {
    changeWorkingAs(null);
    if (userType === 'creator') setIsCreatorListeningMode(true);
    else if (userType === 'label' || userType === 'label_team_member') setIsLabelListeningMode(true);
    else if (userType === 'publisher') setIsPublisherListeningMode(true);
    else if (userType === 'podcaster') setIsPodcasterListeningMode(true);
  }

  const exitPodcasterListeningMode = () => {
    setIsPodcasterListeningMode(false);
  }

  const exitPublisherListeningMode = () => {
    setIsPublisherListeningMode(false);
  }

  // The one definition of "this user is in the listening experience" -
  // listeners always; every other account type only from its listening mode
  // (platform staff never). Use these instead of re-deriving it per component.
  const isListening = !workingAs && (userType === 'listener'
    || (userType === 'creator' && (isCreatorListeningMode || isActListeningMode))
    || ((userType === 'label' || userType === 'label_team_member') && isLabelListeningMode)
    || (userType === 'publisher' && isPublisherListeningMode)
    || (userType === 'podcaster' && isPodcasterListeningMode));
  // A non-listener account currently in one of its listening modes
  const isInListeningMode = isListening && userType !== 'listener';
  // The kind of organization this person is acting for right now - 'label',
  // 'publisher' or null. Use this, not the account type: labels and
  // publishers are run from ordinary accounts. (Older label and publisher
  // logins count too, on their own dashboard.)
  const actingAs = workingAs?.kind
    || (!isListening && (userType === 'label' || userType === 'label_team_member') ? 'label' : null)
    || (!isListening && userType === 'publisher' ? 'publisher' : null);
  // The messages page for who they're acting as
  const messagesRoute = actingAs ? `/${actingAs}/messages`
    : ({ creator: '/creator/messages', podcaster: '/podcaster/messages' }[userType] || '/messages');
  // Where "home" is when not listening: the organization being worked as,
  // or the account's own dashboard
  const dashboardRoute = workingAs
    ? (workingAs.kind === 'label' ? AppRoutes.LabelDash : AppRoutes.PublisherDash)
    : getDashboardRoute(userType);

  return <AuthContext.Provider value={{
    isListening,
    isInListeningMode,
    curatingLabel,
    isAuthed, 
    user, 
    userType, 
    isLoading, 
    isCreatorListeningMode,
    isActListeningMode,
    isLabelListeningMode,
    isPublisherListeningMode,
    isPodcasterListeningMode,
    currentActId,
    workingAs,
    actingAs,
    messagesRoute,
    dashboardRoute,
    workAsOrganization,
    workAsAccount,
    listenAsMe,
    authorizeUser, 
    deauthorizeUser,
    refreshUser,
    enterCreatorListeningMode,
    exitCreatorListeningMode,
    enterActListeningMode,
    exitActListeningMode,
    enterLabelListeningMode,
    exitLabelListeningMode,
    enterPublisherListeningMode,
    exitPublisherListeningMode,
    enterPodcasterListeningMode,
    exitPodcasterListeningMode
  }}>{children}</AuthContext.Provider>;
}

export function useAuthProvider() {
  return useContext(AuthContext);
}
// On a label or publisher dashboard: act as that organization, so the page
// isn't shown inside the listening layout when opened from a link or menu
export function useWorkAs(kind, organization) {
  const { userType, isListening, workingAs, workAsOrganization } = useContext(AuthContext);
  const id = organization?.id;
  const name = organization?.name;
  useEffect(() => {
    if (!id) return;
    // An account that signed up as this kind, on its own dashboard, is
    // already acting as it
    const ownKind = userType === 'publisher' ? 'publisher'
      : (userType === 'label' || userType === 'label_team_member') ? 'label' : null;
    const showingThis = (workingAs?.kind === kind && workingAs?.id === id)
      || (!workingAs && !isListening && ownKind === kind);
    if (!showingThis) workAsOrganization({ kind, id, name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, id, isListening, workingAs?.kind, workingAs?.id]);
}
